import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
  Modal,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import { agreementStatusOptions } from '../../../../../shared/Cis/CisOptions';
import { connect } from 'react-redux';
import { globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator } from './reducer';
import {
  createIpmaCisCreator,
  loadGlobalCisAgreementsAttributesCreator,
  setIpmaCisCreateErrorsCreator,
} from '../../../../../store/actionsCreators';
import { toApiDate } from 'helpers/date';

const AddAgreement = ({
  errors,
  createAgreement,
  removeError,
  memberAssociations,
  levelDomains,
  loadAgreementsAttributes,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  useEffect(() => {
    loadAgreementsAttributes();
  }, []);

  const submit = (levelDomainAll) => {
    const params = {
      remoteMemberAssociation: state.remoteMemberAssociation,
      memberAssociation: state.memberAssociation,
      validFrom: state.validFrom ? toApiDate(state.validFrom) : null,
      validTo: state.validTo ? toApiDate(state.validTo) : null,
    };

    if (levelDomainAll) {
      params.levelDomainAll = true;
    } else {
      params.levelDomain = state.levelDomain;
      params.status = state.status;
    }

    createAgreement(params, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.agreements.addAgreement')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.agreements.memberAssociation')}
                  required
                  error={errors.memberAssociation}
                >
                  <Select
                    id="memberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.agreements.memberAssociationPlaceholder')}
                    value={
                      memberAssociations.find(
                        (option) => option.value === state.memberAssociation,
                      ) || ''
                    }
                    options={memberAssociations}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="remoteMemberAssociation"
                  label={t('page.user.cis.agreements.remoteMemberAssociation')}
                  required
                  error={errors.remoteMemberAssociation}
                >
                  <Select
                    id="remoteMemberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.agreements.remoteMemberAssociationPlaceholder')}
                    value={
                      memberAssociations.find(
                        (option) => option.value === state.remoteMemberAssociation,
                      ) || ''
                    }
                    options={memberAssociations}
                    onChange={handleChangeSelect(
                      'remoteMemberAssociation',
                      'remoteMemberAssociation',
                    )}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="levelDomain"
                  label={t('page.user.cis.agreements.levelDomain')}
                  required
                  error={errors.levelDomain}
                >
                  <Select
                    id="levelDomain"
                    type="text"
                    placeholder={t('page.user.cis.agreements.levelDomainPlaceholder')}
                    value={levelDomains.find((option) => option.value === state.levelDomain) || ''}
                    options={levelDomains}
                    onChange={handleChangeSelect('levelDomain', 'levelDomain')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="status"
                  label={t('page.user.cis.agreements.status')}
                  required
                  error={errors.status}
                >
                  <Select
                    id="status"
                    type="text"
                    placeholder={t('page.user.cis.agreements.statusPlaceholder')}
                    value={
                      agreementStatusOptions.find((option) => option.value === state.status) || ''
                    }
                    options={agreementStatusOptions}
                    onChange={handleChangeSelect('status', 'status')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validFrom"
                  label={t('page.user.cis.agreements.validFrom')}
                  value={state.validFrom}
                  clear={() => handleChangeCustom('validFrom')(null)}
                  error={errors.validFrom}
                >
                  <Datepicker
                    id="validFrom"
                    small
                    placeholderText={t('page.user.cis.agreements.validFromPlaceholder')}
                    selected={state.validFrom}
                    onChange={handleChangeCustom('validFrom', 'validFrom')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="validTo"
                  label={t('page.user.cis.agreements.validTo')}
                  value={state.validTo}
                  clear={() => handleChangeCustom('validTo')(null)}
                  error={errors.validTo}
                >
                  <Datepicker
                    id="validTo"
                    small
                    placeholderText={t('page.user.cis.agreements.validToPlaceholder')}
                    selected={state.validTo}
                    onChange={handleChangeCustom('validTo', 'validTo')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Button type="button" onClick={() => setOpenConfirmModal(true)}>
                {t('page.user.cis.agreements.allowAllLevelDomains')}
              </Button>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit(false)}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
          <Modal
            heading={t('page.user.cis.agreements.confirmAllowAllLevelDomains')}
            isOpen={openConfirmModal}
            confirm={() => submit(true)}
            cancel={() => setOpenConfirmModal(false)}
            onRequestClose={() => setOpenConfirmModal(false)}
          />
        </form>
      </Container>
    </PageContent>
  );
};

AddAgreement.propTypes = {
  errors: PropTypes.shape({
    removeMemberAssociation: PropTypes.string,
    memberAssociation: PropTypes.string,
    levelDomain: PropTypes.string,
    validFrom: PropTypes.string,
    validTo: PropTypes.string,
    status: PropTypes.string,
  }),
  createAgreement: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  memberAssociations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  levelDomains: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadAgreementsAttributes: PropTypes.func.isRequired,
};

AddAgreement.defaultProps = {
  errors: {},
};

const mapStateToProps = ({ ipma, globals }) => ({
  errors: ipma.cis.agreements.createErrors,
  memberAssociations: globals.availableMA,
  levelDomains: globals.cis.levelDomains,
});

const mapDispatchToProps = (dispatch) => ({
  createAgreement: (payload, history) =>
    dispatch(createIpmaCisCreator('agreements', payload, history)),
  removeError: (payload) => dispatch(setIpmaCisCreateErrorsCreator('agreements', payload)),
  loadAgreementsAttributes: () => dispatch(loadGlobalCisAgreementsAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddAgreement);
